import { db } from "../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { sanitizePayload } from "../utils/sanitizer";
import { logSecurityEvent } from "./AuditLogger";

const CORE_COLLECTIONS = [
  "users",
  "pos",
  "vendors",
  "customers",
  "inventory",
  "logistics",
  "opportunities",
  "tasks",
  "tickets",
  "product_tags"
];

/**
 * Generate and download a complete system backup JSON snapshot
 */
export async function generateSystemBackup(userEmail = "admin@jimini.com") {
  try {
    const backupData = {
      system: "Jimini Logistics Enterprise (Atlas Command)",
      schemaVersion: "2026.1",
      complianceStandard: "SOC 2 Type 2 / ISO/IEC 27001 / CMMC Level 1",
      exportedAt: new Date().toISOString(),
      exportedBy: userEmail,
      recordCounts: {},
      collections: {}
    };

    let totalRecords = 0;

    for (const colName of CORE_COLLECTIONS) {
      try {
        const snap = await getDocs(collection(db, colName));
        const items = [];
        snap.forEach((doc) => {
          const raw = { id: doc.id, ...doc.data() };
          // Scrub sensitive secrets from backup payload
          const sanitized = sanitizePayload(raw);
          items.push(sanitized);
        });

        backupData.collections[colName] = items;
        backupData.recordCounts[colName] = items.length;
        totalRecords += items.length;
      } catch (colErr) {
        console.warn(`Could not backup collection ${colName}:`, colErr);
        backupData.collections[colName] = [];
        backupData.recordCounts[colName] = 0;
      }
    }

    backupData.totalRecords = totalRecords;

    // Trigger browser file download
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `jimini-enterprise-backup-${new Date().toISOString().split("T")[0]}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Log security event
    logSecurityEvent({
      action: "SYSTEM_BACKUP_GENERATED",
      category: "ADMIN",
      userEmail: userEmail,
      status: "SUCCESS",
      severity: "INFO",
      details: { totalRecords, collectionsCount: CORE_COLLECTIONS.length }
    });

    return { success: true, totalRecords, recordCounts: backupData.recordCounts };
  } catch (err) {
    console.error("Failed to generate system backup:", err);
    throw err;
  }
}

/**
 * Execute non-destructive schema & referential integrity audit
 * @returns {Promise<{ score: number, checks: Array<{ name: string, status: 'PASS' | 'WARN' | 'FAIL', message: string }> }>}
 */
export async function runSystemIntegrityCheck() {
  const checks = [];
  let passedChecks = 0;

  try {
    // 1. Fetch data for cross-referencing
    const usersSnap = await getDocs(collection(db, "users"));
    const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const posSnap = await getDocs(collection(db, "pos"));
    const pos = posSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const customersSnap = await getDocs(collection(db, "customers"));
    const customers = customersSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const inventorySnap = await getDocs(collection(db, "inventory"));
    const inventory = inventorySnap.docs.map(d => ({ id: d.id, ...d.data() }));

    const opportunitiesSnap = await getDocs(collection(db, "opportunities"));
    const opportunities = opportunitiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // Check 1: User Roles & Schema Validation
    const invalidUsers = users.filter(u => !u.roles || !Array.isArray(u.roles) || u.roles.length === 0);
    if (invalidUsers.length === 0) {
      checks.push({ name: "User Roles Schema Integrity", status: "PASS", message: `All ${users.length} user records have valid role arrays assigned.` });
      passedChecks++;
    } else {
      checks.push({ name: "User Roles Schema Integrity", status: "WARN", message: `${invalidUsers.length} user accounts have unassigned or malformed roles.` });
    }

    // Check 2: Customer Referential Linkage on Won POs
    const customerNames = new Set(customers.map(c => (c.name || "").trim().toLowerCase()));
    const unlinkedPos = pos.filter(p => p.customer && !customerNames.has(p.customer.trim().toLowerCase()));
    if (unlinkedPos.length === 0) {
      checks.push({ name: "Customer-Estimate Referential Integrity", status: "PASS", message: `All ${pos.length} estimates are linked to valid registered customer profiles.` });
      passedChecks++;
    } else {
      checks.push({ name: "Customer-Estimate Referential Integrity", status: "WARN", message: `${unlinkedPos.length} estimates reference unlinked/manual customer names.` });
      passedChecks += 0.5;
    }

    // Check 3: Inventory Line-Item Linkage to POs
    const poIds = new Set(pos.map(p => p.id));
    const poNumbers = new Set(pos.map(p => (p.poNumber || "").trim()));
    const orphanInventory = inventory.filter(item => item.poId && !poIds.has(item.poId) && !poNumbers.has(item.poId));
    if (orphanInventory.length === 0) {
      checks.push({ name: "Inventory-PO Linkage Integrity", status: "PASS", message: `All ${inventory.length} inventory records are linked to active Purchase Orders.` });
      passedChecks++;
    } else {
      checks.push({ name: "Inventory-PO Linkage Integrity", status: "WARN", message: `${orphanInventory.length} inventory items reference non-existent PO IDs.` });
      passedChecks += 0.5;
    }

    // Check 4: Opportunity Assigned Leads Validation
    const userNames = new Set(users.map(u => (u.name || "").trim().toLowerCase()));
    const unassignedOpps = opportunities.filter(o => o.assignedLead && !userNames.has(o.assignedLead.trim().toLowerCase()));
    if (unassignedOpps.length === 0) {
      checks.push({ name: "Sales Pipeline Lead Assignments", status: "PASS", message: `All ${opportunities.length} opportunities have valid active BD lead assignments.` });
      passedChecks++;
    } else {
      checks.push({ name: "Sales Pipeline Lead Assignments", status: "WARN", message: `${unassignedOpps.length} opportunities have inactive or unlinked lead assignees.` });
      passedChecks += 0.5;
    }

    // Check 5: Pricing Calculation Integrity on Won Estimates
    const corruptPricingPos = pos.filter(p => p.status === "Won" && (!p.items || !Array.isArray(p.items) || p.items.some(it => isNaN(Number(it.clientPrice || it.price)))));
    if (corruptPricingPos.length === 0) {
      checks.push({ name: "Won Estimates Pricing Matrix Integrity", status: "PASS", message: "All won estimates contain valid numerical line-item pricing formulas." });
      passedChecks++;
    } else {
      checks.push({ name: "Won Estimates Pricing Matrix Integrity", status: "FAIL", message: `${corruptPricingPos.length} won estimates contain NaN or corrupted line prices.` });
    }

    const totalChecks = checks.length;
    const score = Math.round((passedChecks / totalChecks) * 100);

    return { score, totalChecks, checks };
  } catch (err) {
    console.error("Integrity audit failure:", err);
    return {
      score: 0,
      totalChecks: 1,
      checks: [{ name: "Database Audit Exception", status: "FAIL", message: `Failed to execute integrity audit: ${err.message}` }]
    };
  }
}
